// Home page · "Find active borrowers in your credit box" illustration
import Image from 'next/image';
import { CircleUserRound, Filter, X } from 'lucide-react';
import DemoWrapper from '@/components/DemoWrapper';

export default function HomeBorrowerTable() {
    return (
        <DemoWrapper className="ex-illo bg-white rounded-md border border-slate-200 ui-shadow overflow-hidden relative">
            <div className="p-6 bg-slate-50/50 min-h-[300px]">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-3">
                    Filtering borrowers by:
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-6">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700 shadow-sm">
                        <Filter size={12} strokeWidth={2} className="text-blue-600" />
                        Nationwide
                        <X size={12} strokeWidth={2} className="hidden sm:block text-blue-400" />
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700 shadow-sm">
                        <Filter size={12} strokeWidth={2} className="text-blue-600" />
                        Private Money
                        <X size={12} strokeWidth={2} className="hidden sm:block text-blue-400" />
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700 shadow-sm">
                        <Filter size={12} strokeWidth={2} className="text-blue-600" />
                        Last 12 months
                        <X size={12} strokeWidth={2} className="hidden sm:block text-blue-400" />
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden sm:grid sm:grid-cols-[repeat(4,auto)] sm:justify-between sm:gap-x-4">
                    {/* Table header - hidden on mobile */}
                    <div className="hidden sm:grid col-span-4 grid-cols-subgrid px-5 py-3 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 tracking-wider">
                        <div>Borrower</div>
                        <div>Entities</div>
                        <div>Recent Loans</div>
                        <div className="text-right">Top Lenders</div>
                    </div>
                    {/* Row 1 - Adam Rigel */}
                    <div className="sm:grid sm:col-span-4 sm:grid-cols-subgrid px-4 sm:px-5 py-4 border-b border-slate-100 sm:border-slate-50 items-start fade-in-up delay-100">
                        <div className="flex sm:block items-center justify-between mb-2 sm:mb-0">
                            <div className="flex items-start gap-2">
                                <div className="h-6 flex items-center text-blue-500 shrink-0">
                                    <CircleUserRound size={16} strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-slate-900 whitespace-nowrap ex-up1">
                                        Adam Rigel
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Florida
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 sm:hidden">
                                <Image src="/api/logo/jwbrealestatecapital.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                                <Image src="/api/logo/archwestcapital.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                                <Image src="/api/logo/renovofinancial.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            </div>
                        </div>
                        <div className="flex sm:block items-center gap-3 mb-1 sm:mb-0">
                            <span className="text-xs text-slate-400 sm:hidden">Entities:</span>
                            <span className="sm:block text-xs font-medium text-slate-900 ex-up1">
                                23 Active
                            </span>
                        </div>
                        <div className="flex sm:block items-center gap-3">
                            <span className="text-xs text-slate-400 sm:hidden">Loans:</span>
                            <div className="flex sm:block items-center gap-2">
                                <div className="text-xs font-medium text-slate-900 ex-up1">
                                    358 Loans
                                </div>
                                <div className="text-xs text-slate-500">
                                    $85M Volume
                                </div>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center justify-end gap-1.5">
                            <Image src="/api/logo/jwbrealestatecapital.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            <Image src="/api/logo/archwestcapital.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            <Image src="/api/logo/renovofinancial.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                        </div>
                    </div>
                    {/* Row 2 - Luis Mata */}
                    <div className="sm:grid sm:col-span-4 sm:grid-cols-subgrid px-4 sm:px-5 py-4 border-b border-slate-100 sm:border-slate-50 items-start fade-in-up delay-200">
                        <div className="flex sm:block items-center justify-between mb-2 sm:mb-0">
                            <div className="flex items-start gap-2">
                                <div className="h-6 flex items-center text-blue-500 shrink-0">
                                    <CircleUserRound size={16} strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-slate-900 whitespace-nowrap ex-up1">
                                        Luis Mata
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Texas
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 sm:hidden">
                                <Image src="/api/logo/corridorfunding.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                                <Image src="/api/logo/loanrangercapital.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                                <Image src="/api/logo/merchantsmtg.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            </div>
                        </div>
                        <div className="flex sm:block items-center gap-3 mb-1 sm:mb-0">
                            <span className="text-xs text-slate-400 sm:hidden">Entities:</span>
                            <span className="sm:block text-xs font-medium text-slate-900 ex-up1">
                                11 Active
                            </span>
                        </div>
                        <div className="flex sm:block items-center gap-3">
                            <span className="text-xs text-slate-400 sm:hidden">Loans:</span>
                            <div className="flex sm:block items-center gap-2">
                                <div className="text-xs font-medium text-slate-900 ex-up1">
                                    244 Loans
                                </div>
                                <div className="text-xs text-slate-500">
                                    $37M Volume
                                </div>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center justify-end gap-1.5">
                            <Image src="/api/logo/corridorfunding.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            <Image src="/api/logo/loanrangercapital.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            <Image src="/api/logo/merchantsmtg.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                        </div>
                    </div>
                    {/* Row 3 - Dominic Carrillo */}
                    <div className="sm:grid sm:col-span-4 sm:grid-cols-subgrid px-4 sm:px-5 py-4 items-start fade-in-up delay-300">
                        <div className="flex sm:block items-center justify-between mb-2 sm:mb-0">
                            <div className="flex items-start gap-2">
                                <div className="h-6 flex items-center text-blue-500 shrink-0">
                                    <CircleUserRound size={16} strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-slate-900 whitespace-nowrap ex-up1">
                                        Dominic Carrillo
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Texas
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 sm:hidden">
                                <Image src="/api/logo/housemaxfunding.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                                <Image src="/api/logo/backflip.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                                <Image src="/api/logo/tuskercap.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            </div>
                        </div>
                        <div className="flex sm:block items-center gap-3 mb-1 sm:mb-0">
                            <span className="text-xs text-slate-400 sm:hidden">Entities:</span>
                            <span className="sm:block text-xs font-medium text-slate-900 ex-up1">
                                17 Active
                            </span>
                        </div>
                        <div className="flex sm:block items-center gap-3">
                            <span className="text-xs text-slate-400 sm:hidden">Loans:</span>
                            <div className="flex sm:block items-center gap-2">
                                <div className="text-xs font-medium text-slate-900 ex-up1">
                                    221 Loans
                                </div>
                                <div className="text-xs text-slate-500">
                                    $78M Volume
                                </div>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center justify-end gap-1.5">
                            <Image src="/api/logo/housemaxfunding.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            <Image src="/api/logo/backflip.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                            <Image src="/api/logo/tuskercap.com?size=32" alt="" width={32} height={32} className="w-6 h-6 rounded-sm" unoptimized />
                        </div>
                    </div>
                </div>
            </div>
        </DemoWrapper>
    );
}
